// DEVICES & KEYS (UI rebuild batch 4 · custody setup · reference build) — Astra's administrative inventory, operational
// facts first: your signing device (the one key the desk takes a signature from), then one row per Ledger account key —
// purpose, network, what it has issued, its state. Register device is the page's one gold button, on its own surface
// (RegisterKey.tsx); Retire is a separate step inside the key's sheet, with its procedure (the friction is kept).
//
// WHO HOLDS WHICH (the operator, 22 Oct 2026, memory project_wallet_key_ownership): the desk operator's new Nano holds
// deposits (account 1), Hot and Merchant Hot; the owner's Nano holds Treasury (2-of-3 later). The API names no device
// per key, so the rule is said once in the page's line, never claimed per row (spec, open question 2).
//
// Kept from screens/custody/Keys.tsx: the reads and keys (['custody','me'], ['custody','keys'], ['custody','balances']
// for the procedure, ['custody','key-holdings', id] read when the confirm opens), keyWord (the server's classWord, else
// the convention's), Retire with replacementPlan's headline and lines, the confirm taking the focus, Escape closing it,
// the refreshes after a retirement.
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus } from 'lucide-react';
import { custody as api, type CustodyKey, type WalletBalance } from '../../../lib/api';
import { fmtDate } from '../../../lib/format';
import { CLASS_WORD } from '../../../lib/custody-format';
import { replacementPlan, HOLDINGS_CURRENT_MS } from '../../../lib/custody-keys-replace';
import { useHoldingsForPlan } from '../../../lib/useHoldingsForPlan';
import { KEY_ACCOUNT_CLASS, accountWord } from '../../../lib/custody-renewals';
import { keyRefusal } from '../../custody/refusals';
import { Pill } from '../../../components/kit';
import {
  PageHead, Panel, Notice, Register, SkeletonRows, StateLine, DetailSheet, Fact, Address, TAP, type RegisterColumn,
} from '../../../components/v2/kit';
import { SectionTitle } from '../../../components/v2/custody/parts';
import { CA } from '../../../components/v2/custody-admin/paths';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/** The class word for a registered key: the server's `classWord` when it sends one, else the convention's for its account. */
export function keyWord(k: CustodyKey): string | null {
  if (typeof k.classWord === 'string' && k.classWord) return k.classWord;
  const cls = k.account === null ? undefined : KEY_ACCOUNT_CLASS[k.account];
  return cls ? CLASS_WORD[cls] ?? cls : null;
}
const purposeOf = (k: CustodyKey): string => keyWord(k) ?? (k.account === null ? 'Account key' : `Account ${k.account + 1}`);
const accountOf = (k: CustodyKey): string => (k.account === null ? 'account not named' : accountWord(k.account).replace(/^a/, 'A'));
const issuedOf = (k: CustodyKey): string => (k.lastIndex < 0 ? 'None issued' : `${k.lastIndex + 1} issued`);

function KeyState({ k }: { k: CustodyKey }) {
  return k.status === 'active' ? <Pill tone="good">Active</Pill> : <Pill tone="neutral">{k.status === 'retired' ? 'Retired' : k.status}</Pill>;
}

/** `embedded`: drawn inside custody's Settings screen as its Devices & keys section. */
export function KeysV2({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const me = useQuery({ queryKey: ['custody', 'me'], queryFn: api.me });
  const keys = useQuery({ queryKey: ['custody', 'keys'], queryFn: api.keys });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, staleTime: 30_000, retry: false });
  const all = keys.data?.keys ?? [];
  // Active keys first, by account; retired after.
  const rows = [...all].sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || (a.account ?? 99) - (b.account ?? 99) || b.id - a.id);
  const openId = params.get('key');
  const open = openId ? all.find((k) => String(k.id) === openId) ?? null : null;
  const setOpen = (id: number | null) => { const n = new URLSearchParams(params); if (id === null) n.delete('key'); else n.set('key', String(id)); setParams(n); };

  const columns: RegisterColumn<CustodyKey>[] = [
    { key: 'purpose', head: 'Key / purpose', cell: (k) => (
      <span className="block min-w-0">
        <span className="block truncate font-medium">{purposeOf(k)}</span>
        <span className="mt-1 block truncate text-meta text-tg-hint">{[accountOf(k), k.label].filter(Boolean).join(' · ')}</span>
      </span>
    ) },
    { key: 'net', head: 'Network', width: 'w-[8rem]', cell: () => <span className="font-mono">TRC20</span> },
    { key: 'issued', head: 'Key state', align: 'right', width: 'w-[12rem]', cell: issuedOf },
    { key: 'state', head: 'State', align: 'right', width: 'w-[10rem]', cell: (k) => <span className="inline-flex items-center gap-2"><KeyState k={k} /><ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden /></span> },
  ];

  return (
    <>
      {embedded ? (
        <SectionTitle title="Devices & keys" action={<Link to={CA.register} className="btn-secondary px-4 text-body">Register device</Link>} />
      ) : (
        <PageHead
          title="Devices & keys"
          inline
          sub="Desk Nano: deposits, Hot, Merchant Hot · Owner's Nano: Treasury"
          actions={(
            <Link to={CA.register} className="btn-primary inline-flex min-h-11 items-center gap-2 px-4 text-body lg:min-h-10">
              <Plus className="h-4 w-4" aria-hidden />Register device
            </Link>
          )}
        />
      )}

      <Panel title="Your signing device" className="mb-4 lg:mb-6">
        {me.isError ? (
          isCustodySignin(me.error) ? <SigninQuiet error={me.error} /> : <StateLine tone="bad" more={textOf(me.error)} action={<button type="button" className={TAP} onClick={() => void me.refetch()}>Try again</button>}>Couldn&rsquo;t read your device.</StateLine>
        ) : !me.data ? (
          <p className="text-body text-tg-hint">Reading…</p>
        ) : me.data.device ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="min-w-0 text-body">Ledger · TRC20 · <Address value={me.data.device} /></span>
            <Pill tone="good">Registered</Pill>
          </div>
        ) : (
          <StateLine tone="warn" more="No signature of yours is taken until a device is registered. The desk registers your Ledger's address; it is not done from here.">
            No signing device registered for you.
          </StateLine>
        )}
      </Panel>

      {keys.isError && !isCustodySignin(keys.error) && (
        <Notice tone="bad">
          <StateLine tone="bad" more={textOf(keys.error)} action={<button type="button" className={TAP} disabled={keys.isFetching} onClick={() => void keys.refetch()}>Try again</button>}>
            {keys.data ? <>Couldn&rsquo;t refresh — showing the last read.</> : <>Couldn&rsquo;t read the keys.</>}
          </StateLine>
        </Notice>
      )}
      {!keys.data && !keys.isError ? (
        <Panel flush><SkeletonRows rows={3} what="keys" /></Panel>
      ) : rows.length > 0 ? (
        <Panel flush>
          <Register
            label="Account keys"
            columns={columns}
            rows={rows}
            rowKey={(k) => String(k.id)}
            onOpen={(k) => setOpen(k.id)}
            card={(k) => ({
              title: <>{purposeOf(k)}<span className="mt-1 block truncate text-meta font-normal text-tg-hint">{[accountOf(k), k.label].filter(Boolean).join(' · ')}</span></>,
              figure: <ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden />,
              meta: <><span className="font-mono">TRC20</span> · {issuedOf(k)}</>,
              state: <KeyState k={k} />,
            })}
          />
        </Panel>
      ) : keys.data && !keys.isError ? (
        <Notice tone="warn">No account key yet — deposit addresses wait for account 1.</Notice>
      ) : null}

      {open && (
        <KeySheet
          k={open}
          floats={balances.data?.wallets ?? null}
          onClose={() => setOpen(null)}
          onRetired={() => { for (const key of ['keys', 'renewals', 'balances']) void qc.invalidateQueries({ queryKey: ['custody', key] }); }}
        />
      )}
    </>
  );
}

/** One key: its facts, and Retire — a separate step with this account's procedure (Screens' keys rounds 3–5). */
function KeySheet({ k, floats, onClose, onRetired }: { k: CustodyKey; floats: ReadonlyArray<WalletBalance> | null; onClose: () => void; onRetired: () => void }) {
  const active = k.status === 'active';
  const word = keyWord(k);
  const [confirming, setConfirming] = useState(false);
  const retire = useMutation({ mutationFn: () => api.retireKey(k.id), onSuccess: onRetired });
  // What THIS key still signs: read on opening and again while open; spoken from only while KNOWN — not after a failed
  // refresh, not once old (Astra round d, #4, ported by the wallet lane); not in, failed or partial → "balance not read".
  const holdings = useQuery({ queryKey: ['custody', 'key-holdings', k.id], queryFn: () => api.keyHoldings(k.id), enabled: confirming, staleTime: 0, refetchOnMount: 'always', refetchInterval: confirming ? HOLDINGS_CURRENT_MS / 2 : false, retry: false });
  // Re-judged the moment the answer goes old, and a paused refresh is unknown (round e #4; lib/useHoldingsForPlan).
  const plan = replacementPlan(k.account, floats, useHoldingsForPlan(holdings, confirming));
  const name = k.label ?? (k.account === null ? 'this key' : accountWord(k.account));
  const confirmRef = useRef<HTMLDivElement>(null);
  const retireRef = useRef<HTMLButtonElement>(null);
  const was = useRef(false);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
    else if (was.current) retireRef.current?.focus();
    was.current = confirming;
  }, [confirming]);

  const footer = active && !retire.isSuccess ? (confirming ? (
    <div className="flex flex-col-reverse gap-2 lg:flex-row lg:justify-end">
      <button type="button" className="btn-secondary min-h-11 px-4 text-body lg:min-h-10" onClick={() => setConfirming(false)}>Keep</button>
      <button type="button" className="btn-primary min-h-11 px-4 text-body lg:min-h-10" disabled={retire.isPending} onClick={() => { retire.mutate(); setConfirming(false); }}>
        {retire.isPending ? 'Retiring…' : `Retire ${name}${word ? ` (${word})` : ''}`}
      </button>
    </div>
  ) : (
    <button ref={retireRef} type="button" className="btn-secondary min-h-11 w-full px-4 text-body lg:min-h-10" disabled={retire.isPending} onClick={() => setConfirming(true)}>
      {retire.isPending ? 'Retiring…' : 'Retire'}
    </button>
  )) : undefined;

  return (
    <DetailSheet title={purposeOf(k)} onClose={onClose} footer={footer}>
      <div className="mb-4 flex items-center gap-2"><KeyState k={k} /></div>
      <div className="border-t border-stroke-soft">
        <Fact label="Ledger account">{accountOf(k)}</Fact>
        {k.label && <Fact label="Label">{k.label}</Fact>}
        <Fact label="Network">TRC20</Fact>
        <Fact label="First address">{k.firstAddress ? <span className="break-all font-mono">{k.firstAddress}</span> : 'not derivable'}</Fact>
        <Fact label="Issued">{issuedOf(k)}</Fact>
        {/* The owner's device (Q4): its head only — the address is public, the habit is the typing gate's. */}
        <Fact label="Device">{k.device ? <span className="font-mono">{k.device.slice(0, 8)}… · {k.ownerTelegramId ?? '—'}</span> : 'not named'}</Fact>
        {k.registeredAt && <Fact label="Registered">{fmtDate(k.registeredAt)}</Fact>}
      </div>
      {retire.isSuccess && <StateLine tone="good" className="mt-4">Retired — its account is free for a new key.</StateLine>}
      {retire.isError && <StateLine tone="bad" className="mt-4" more={keyRefusal(retire.error)}>Not retired — try again.</StateLine>}
      {active && confirming && (
        <div
          ref={confirmRef}
          tabIndex={-1}
          role="group"
          aria-label={`Retire ${name}`}
          className="mt-4 rounded-lg border border-stroke p-3 outline-none focus-visible:ring-2 focus-visible:ring-tg-accent"
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setConfirming(false); } }}
        >
          <StateLine tone="warn" more={<ul className="list-disc space-y-1 pl-4">{plan.lines.map((l) => <li key={l}>{l}</li>)}</ul>}>{plan.headline}</StateLine>
          {holdings.isError && !isCustodySignin(holdings.error) && (
            <StateLine tone="bad" className="mt-1" action={<button type="button" className="btn-link min-h-11 text-body" onClick={() => void holdings.refetch()} disabled={holdings.isFetching}>{holdings.isFetching ? 'Reading…' : 'Try again'}</button>}>
              Couldn&apos;t read what this key holds.
            </StateLine>
          )}
        </div>
      )}
    </DetailSheet>
  );
}
